import {
  FC,
  useCallback,
  useMemo,
  useState,
  useRef,
  useEffect,
  forwardRef,
} from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FixedSizeList as List } from "react-window";

import styles from "./GlobalSearch.module.scss";
import { useModal, useTrackerBlocker } from "src/hooks";
import { LockOutline, TrashOutline, User, Loader } from "src/assets/icons";
import { SelectOption } from "src/components/selectors/Select/types";
import {
  ROUTES,
  TRACKER_SORT_OPTIONS,
  SEARCH_KEYWORDS_DATA_SOURCE_MAP,
} from "src/constants";
import {
  sortTrackers,
  getIconByName,
  filterTrackers,
  sortDashboards,
  getUserFullName,
  filterDashboards,
  isKeywordsDataSourceTypeGuard,
  formatDashboardsWithKeywordsDataSources,
} from "src/utils";
import {
  Button,
  Select,
  Sidebar,
  Tooltip,
  InputWithIcon,
  Preloader,
  Tabs,
} from "src/components";
import {
  selectUser,
  selectCompanyMembers,
  selectApplicationInfo,
  selectCompanyTrackers,
  selectUserPermissions,
  selectVisibleDashboards,
  selectTrackersCollections,
  selectSubscriptionPlanKeywordsDataSources,
  selectDashboardsStatus,
  selectTrackersStatus,
} from "src/store/selectors";
import {
  TrackerStatusIcon,
  ConfirmDeleteTrackerModal,
  ConfirmDashboardDeletionModal,
  TrackerKeywordsDataSourcesIcon,
  DashboardKeywordsDataSourcesIcon,
} from "src/features";
import { useGlobalSearchLoader } from "src/containers/Home/hooks/mainAppDataLoaders";

// Inner imports
import type { GlobalSearchProps } from "./types";

// Constants for virtualized list
const ITEM_HEIGHT = 56; // Set height based on your item's height (56px)

// Tab constants
const TAB_DASHBOARDS = "dashboards";
const TAB_TRACKERS = "trackers";

// Outer element for virtualized lists for accessibility
const ListContainer = forwardRef<
  HTMLDivElement,
  React.HTMLProps<HTMLDivElement>
>((props, ref) => <div ref={ref} {...props} role="list" />);

// Dashboard Item Component for virtualization
const DashboardItem: FC<{
  dashboard: Dashboard.Data;
  index: number;
  style: React.CSSProperties;
  showStatusIcon: (id: string) => JSX.Element | null;
  getAuthorName: (id: string) => string;
  canDelete: (dashboard: Dashboard.Data) => boolean;
  onDashboardClick: (id: string) => void;
  onDeleteClick: (id: string) => void;
  t: (key: string) => string;
}> = ({
  dashboard,
  style,
  showStatusIcon,
  getAuthorName,
  canDelete,
  onDashboardClick,
  onDeleteClick,
  t,
}) => (
  <div
    className={styles.sectionItem}
    style={style}
    key={dashboard.id}
    role="listitem"
    onClick={() => onDashboardClick(dashboard.id)}
  >
    <button
      type="button"
      className={styles.itemButton}
      onClick={(e) => {
        e.stopPropagation(); // Prevent double navigation
        onDashboardClick(dashboard.id);
      }}
      aria-label={`View dashboard: ${dashboard.name}`}
    >
      <div className={styles.sectionItemIcon}>
        <DashboardKeywordsDataSourcesIcon trackersCollectionId={dashboard.id} />
      </div>
      <div className={styles.sectionItemName}>
        <span>{dashboard.name}</span>
        {showStatusIcon(dashboard.id)}
      </div>
    </button>
    <div className={styles.sectionItemActionsHover}>
      <div className={styles.authorWrapper}>
        <User />
        <span>{getAuthorName(dashboard.authorId)}</span>
      </div>
      {dashboard.isLocked && (
        <Tooltip
          content={t(
            "component.sidebar.global_search.tooltip.locked_dashboard",
          )}
        >
          <span className={styles.lockIcon}>
            <LockOutline />
          </span>
        </Tooltip>
      )}
      {canDelete(dashboard) && (
        <Tooltip
          content={t(
            "component.sidebar.global_search.tooltip.delete_dashboard",
          )}
        >
          <button
            type="button"
            className={styles.itemDeleteButton}
            onClick={(e) => {
              e.stopPropagation(); // Prevent row click when delete button is clicked
              onDeleteClick(dashboard.id);
            }}
            aria-label={`Delete dashboard: ${dashboard.name}`}
          >
            <TrashOutline />
          </button>
        </Tooltip>
      )}
    </div>
  </div>
);

// Tracker Item Component for virtualization
const TrackerItem: FC<{
  tracker: Tracker.Data;
  index: number;
  style: React.CSSProperties;
  getAuthorName: (id: string) => string;
  onTrackerClick: (id: string) => void;
  onDeleteClick: (id: string) => void;
  t: (key: string) => string;
}> = ({ tracker, style, getAuthorName, onTrackerClick, onDeleteClick, t }) => (
  <div
    className={styles.sectionItem}
    style={style}
    key={tracker.id}
    role="listitem"
    onClick={() => onTrackerClick(tracker.id)}
  >
    <button
      type="button"
      className={styles.itemButton}
      onClick={(e) => {
        e.stopPropagation(); // Prevent double navigation
        onTrackerClick(tracker.id);
      }}
      aria-label={`View topic: ${tracker.name}`}
    >
      <div className={styles.sectionItemIcon}>
        <TrackerKeywordsDataSourcesIcon trackerId={tracker.id} />
      </div>
      <div className={styles.sectionItemName}>
        <span>{tracker.name}</span>
        <TrackerStatusIcon trackerId={tracker.id} />
      </div>
    </button>
    <div className={styles.sectionItemActionsHover}>
      <div className={styles.authorWrapper}>
        <User />
        <span>{getAuthorName(tracker.authorId)}</span>
      </div>
      <Tooltip
        content={t("component.sidebar.global_search.tooltip.delete_tracker")}
      >
        <button
          type="button"
          className={styles.itemDeleteButton}
          onClick={(e) => {
            e.stopPropagation(); // Prevent row click when delete button is clicked
            onDeleteClick(tracker.id);
          }}
          aria-label={`Delete topic: ${tracker.name}`}
        >
          <TrashOutline />
        </button>
      </Tooltip>
    </div>
  </div>
);

// Loading Item Component for loading states
const LoadingItem: FC<{
  style: React.CSSProperties;
}> = ({ style }) => (
  <div className={styles.sectionItem} style={style} role="progressbar">
    <div className={styles.loadingItem}>
      <Preloader type="bar" />
    </div>
  </div>
);

export const GlobalSearch: FC<GlobalSearchProps> = ({
  isOpened,
  setIsOpened,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { setModal } = useModal();
  const dashboardsListRef = useRef<List>(null);
  const trackersListRef = useRef<List>(null);
  const [activeTab, setActiveTab] = useState<string>(TAB_DASHBOARDS);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);

  const { hasTrackerAccess, showSubscriptionPlanLimitNotification } =
    useTrackerBlocker();

  const user = useSelector(selectUser);
  const trackers = useSelector(selectCompanyTrackers);
  const trackersCollections = useSelector(selectTrackersCollections);
  const dashboards = useSelector(selectVisibleDashboards);
  const { isUserCompanyOwnerOrAdmin } = useSelector(selectUserPermissions);
  const { trackersWithPartialData } = useSelector(selectApplicationInfo);
  const companyMembers = useSelector(selectCompanyMembers);
  const dashboardsLoadingStatus = useSelector(selectDashboardsStatus);
  const trackersLoadingStatus = useSelector(selectTrackersStatus);
  const subscriptionPlanKeywordsDataSources = useSelector(
    selectSubscriptionPlanKeywordsDataSources,
  );

  const [searchQuery, setSearchQuery] = useState<string>("");
  const [sortType, setSortType] = useState<Tracker.ListSort>("lastViewed");
  const [filterMemberId, setFilterMemberId] =
    useState<Company.Member["id"]>("all");
  const [filterKeywordsDataSource, setFilterKeywordsDataSource] = useState<
    Search.KeywordsDataSource | "all"
  >("all");

  // Reset list positions when filters change
  useEffect(() => {
    if (dashboardsListRef.current) {
      dashboardsListRef.current.scrollTo(0);
    }
    if (trackersListRef.current) {
      trackersListRef.current.scrollTo(0);
    }
  }, [searchQuery, filterMemberId, filterKeywordsDataSource, sortType]);

  const keywordsDataSourceOptions = useMemo<SelectOption[]>(() => {
    const options = new Set<SelectOption>();

    options.add({
      value: "all",
      label: t("component.sidebar.global_search.form.data_source.all"),
    });

    for (const dataSource of subscriptionPlanKeywordsDataSources) {
      if (!isKeywordsDataSourceTypeGuard(dataSource)) continue;

      options.add({
        value: dataSource,
        label: t(SEARCH_KEYWORDS_DATA_SOURCE_MAP[dataSource].labelKey),
        renderIcon: () =>
          getIconByName(SEARCH_KEYWORDS_DATA_SOURCE_MAP[dataSource].icon),
      });
    }

    return Array.from(options);
  }, [subscriptionPlanKeywordsDataSources, t]);

  const companyMemberOptions = useMemo<SelectOption[]>(() => {
    const options = new Set<SelectOption>();

    options.add({
      value: "all",
      label: t("component.sidebar.global_search.form.author_id.all"),
    });

    const memberOptions = new Set<SelectOption>();

    for (const memberId in companyMembers) {
      const member = companyMembers[memberId];

      if (!member) continue;

      const memberName =
        memberId === user.id
          ? t("component.sidebar.global_search.form.author_id.current_user", {
              name: getUserFullName(member),
            })
          : getUserFullName(member);

      memberOptions.add({ value: memberId, label: memberName });
    }

    const sortedMemberOptions = Array.from(memberOptions).sort(
      (a, b) => Number(b.value === user.id) - Number(a.value === user.id),
    );

    return [...options, ...sortedMemberOptions];
  }, [companyMembers, t, user.id]);

  const formattedDashboards = useMemo<Dashboard.DataWithKeywordsDataSources[]>(
    () =>
      formatDashboardsWithKeywordsDataSources({
        trackers,
        dashboards,
        trackersCollections,
      }),
    [dashboards, trackers, trackersCollections],
  );

  const filteredDashboards = useMemo<Dashboard.Data[]>(
    () =>
      filterDashboards({
        query: searchQuery,
        authorId: filterMemberId,
        dashboards: formattedDashboards,
        keywordsDataSource: filterKeywordsDataSource,
      }),
    [
      searchQuery,
      filterMemberId,
      formattedDashboards,
      filterKeywordsDataSource,
    ],
  );

  const sortedDashboards = useMemo<Dashboard.Data[]>(
    () =>
      sortDashboards({
        type: sortType,
        dashboards: filteredDashboards,
        lastViewedDashboardIds: user.lastViewedDashboardIds,
      }),
    [filteredDashboards, sortType, user.lastViewedDashboardIds],
  );

  const filteredTrackers = useMemo<Tracker.Data[]>(
    () =>
      filterTrackers({
        trackers,
        query: searchQuery,
        authorId: filterMemberId,
        keywordsDataSource: filterKeywordsDataSource,
      }),
    [trackers, searchQuery, filterMemberId, filterKeywordsDataSource],
  );

  const sortedTrackers = useMemo<Tracker.Data[]>(
    () =>
      sortTrackers({
        type: sortType,
        trackers: filteredTrackers,
        lastViewedTrackerIds: user.lastViewedTrackerIds,
      }),
    [sortType, filteredTrackers, user.lastViewedTrackerIds],
  );

  const { loadAllData, hasLoadedAll: hasLoadedAllData } =
    useGlobalSearchLoader();

  const isLoadingDashboards = useMemo(() => {
    if (isLoadingMore) return false;
    return dashboardsLoadingStatus === "loading";
  }, [dashboardsLoadingStatus, isLoadingMore]);

  const isLoadingTrackers = useMemo(() => {
    return trackersLoadingStatus === "loading";
  }, [trackersLoadingStatus]);

  const closeSidebar = useCallback(
    (): void => setIsOpened(false),
    [setIsOpened],
  );

  const showDashboardStatusIcon = useCallback(
    (dashboardId: Dashboard.Data["id"]): JSX.Element | null => {
      const trackersCollection = trackersCollections.find(
        ({ id }) => id === dashboardId,
      );

      if (!trackersCollection) return null;

      const trackerStatuses = trackersCollection.trackerIds.map(
        (id) => trackersWithPartialData[id],
      );

      switch (true) {
        case trackerStatuses.includes("NO_SELECTED_KEYWORDS"): {
          const trackerIdWithStatus = trackersCollection.trackerIds.find(
            (id) => trackersWithPartialData[id] === "NO_SELECTED_KEYWORDS",
          );

          if (!trackerIdWithStatus) return null;

          return <TrackerStatusIcon trackerId={trackerIdWithStatus} />;
        }

        case trackerStatuses.includes("INCOMPLETE_DATA"): {
          const trackerIdWithStatus = trackersCollection.trackerIds.find(
            (id) => trackersWithPartialData[id] === "INCOMPLETE_DATA",
          );

          if (!trackerIdWithStatus) return null;

          return <TrackerStatusIcon trackerId={trackerIdWithStatus} />;
        }

        default:
          return null;
      }
    },
    [trackersCollections, trackersWithPartialData],
  );

  const getAuthorFullName = useCallback(
    (value: User.Data["id"]): string => {
      const member = companyMembers[value];

      if (!member) return "";

      if (user.id === value)
        return t("component.sidebar.global_search.label.current_user", {
          name: getUserFullName(member),
        });

      return getUserFullName(member);
    },
    [companyMembers, t, user.id],
  );

  const getIsDeleteDashboardAvailable = useCallback(
    (dashboard: Dashboard.Data): boolean => {
      if (!dashboard.isLocked) return true;

      return user.id === dashboard.authorId || isUserCompanyOwnerOrAdmin;
    },
    [isUserCompanyOwnerOrAdmin, user.id],
  );

  const onDashboardClick = (value: Dashboard.Data["id"]): void => {
    history.push(`${ROUTES.dashboardsHomePage}/${value}`);

    closeSidebar();
  };

  const onDashboardDeleteClick = (value: Dashboard.Data["id"]): void =>
    setModal(
      "confirm-dashboard-deletion",
      <ConfirmDashboardDeletionModal trackersCollectionId={value} />,
    );

  const onTrackerClick = (value: Tracker.Data["id"]): void => {
    if (!hasTrackerAccess) return showSubscriptionPlanLimitNotification();

    history.push(`${ROUTES.trackersHomePage}/edit/${value}`);

    closeSidebar();
  };

  const onTrackerDeleteClick = (value: Tracker.Data["id"]): void =>
    setModal(
      "confirm-delete-tracker",
      <ConfirmDeleteTrackerModal trackerId={value} />,
    );

  const onSortTypeChange = (value: Tracker.ListSort): void =>
    setSortType(value);

  const onChangeFilterMemberId = (value: Company.Member["id"]): void =>
    setFilterMemberId(value);

  const onChangeFilterKeywordsDataSource = (value: string): void => {
    if (!isKeywordsDataSourceTypeGuard(value))
      return setFilterKeywordsDataSource("all");

    setFilterKeywordsDataSource(value);
  };

  const handleLoadMore = useCallback(() => {
    setIsLoadingMore(true);

    loadAllData()
      .catch((error) => {
        console.error("Error loading all data:", error);
      })
      .finally(() => {
        setIsLoadingMore(false);
      });
  }, [loadAllData]);

  const tabs = useMemo(() => {
    return [
      {
        value: TAB_DASHBOARDS,
        label: t("component.sidebar.global_search.label.dashboards"),
        onSelect: (value: string) => setActiveTab(value),
      },
      {
        value: TAB_TRACKERS,
        label: t("component.sidebar.global_search.label.trackers"),
        onSelect: (value: string) => setActiveTab(value),
      },
    ];
  }, [t]);

  return (
    <Sidebar isSidebarOpen={isOpened} setIsSidebarOpen={setIsOpened}>
      <div className={styles.wrapper}>
        <form
          className={styles.form}
          role="search"
          aria-label="Search dashboards and topics"
        >
          <div className={styles.formGroup}>
            <InputWithIcon
              tabIndex={1}
              autoFocus
              icon="Magnifier"
              hasClearButton
              value={searchQuery}
              placeholder={t(
                "component.sidebar.global_search.form.placeholder.search_query",
              )}
              changeHandler={setSearchQuery}
              aria-label="Search query"
            />
          </div>
          <div className={styles.formGroup}>
            <Select
              icon="User"
              hasFilter={false}
              value={filterMemberId}
              options={companyMemberOptions}
              changeHandler={onChangeFilterMemberId}
              aria-label="Filter by owner"
            />
            <Select
              icon="Filter"
              hasFilter={false}
              options={keywordsDataSourceOptions}
              value={filterKeywordsDataSource || ""}
              changeHandler={onChangeFilterKeywordsDataSource}
              aria-label="Filter by data source"
            />
          </div>
          <div className={`${styles.formGroup} ${styles.sortFormGroup}`}>
            <Select
              icon="Sort"
              value={sortType}
              options={TRACKER_SORT_OPTIONS}
              openingDirection="bottom-end"
              inputClassName={styles.sortInput}
              selectClassName={styles.sortSelect}
              changeHandler={(value: string) =>
                onSortTypeChange(value as Tracker.ListSort)
              }
              aria-label="Sort by"
            />
          </div>
        </form>

        <div className={styles.tabs} role="tablist">
          <Tabs
            tabs={tabs}
            activeTab={activeTab}
            className={`${styles.tabsComponent} ${styles.noBorder}`}
          />
        </div>

        <div
          className={styles.content}
          role="tabpanel"
          aria-label={
            activeTab === TAB_DASHBOARDS ? "Dashboards tab" : "Topics tab"
          }
        >
          {activeTab === TAB_DASHBOARDS ? (
            <div className={styles.section}>
              <div className={styles.sectionItems} aria-label="Dashboard list">
                {isLoadingDashboards && sortedDashboards.length === 0 ? (
                  Array.from({ length: 5 }).map((_, index) => (
                    <div
                      className={styles.sectionItem}
                      key={`loading-dash-${index}`}
                      role="progressbar"
                    >
                      <div className={styles.loadingItem}>
                        <Preloader type="bar" />
                      </div>
                    </div>
                  ))
                ) : sortedDashboards.length ? (
                  <List
                    ref={dashboardsListRef}
                    height={
                      sortedDashboards.length * ITEM_HEIGHT +
                      (!hasLoadedAllData ? ITEM_HEIGHT : 0)
                    }
                    itemCount={
                      sortedDashboards.length + (!hasLoadedAllData ? 1 : 0)
                    }
                    itemSize={ITEM_HEIGHT}
                    width="100%"
                    className={styles.virtualizedList}
                    outerElementType={ListContainer}
                  >
                    {({
                      index,
                      style,
                    }: {
                      index: number;
                      style: React.CSSProperties;
                    }) => {
                      if (isLoadingDashboards && !isLoadingMore) {
                        return <LoadingItem style={style} />;
                      }

                      if (index === sortedDashboards.length) {
                        // Render "Load More" button if not all loaded AND not triggered yet
                        return !hasLoadedAllData ? (
                          <div className={styles.loadMoreButton} style={style}>
                            <Button
                              onClick={handleLoadMore}
                              buttonSize="small"
                              disabled={isLoadingMore}
                              aria-label="Load more dashboards"
                            >
                              {isLoadingMore ? (
                                <Loader className={styles.loader} />
                              ) : (
                                t("component.sidebar.global_search.load_more")
                              )}
                            </Button>
                          </div>
                        ) : null;
                      }

                      const dashboard = sortedDashboards[index];
                      if (!dashboard) return null;

                      return (
                        <DashboardItem
                          dashboard={dashboard}
                          index={index}
                          style={style}
                          showStatusIcon={showDashboardStatusIcon}
                          getAuthorName={getAuthorFullName}
                          canDelete={getIsDeleteDashboardAvailable}
                          onDashboardClick={onDashboardClick}
                          onDeleteClick={onDashboardDeleteClick}
                          t={t}
                        />
                      );
                    }}
                  </List>
                ) : (
                  <div className={styles.emptyStateContainer}>
                    <div className={styles.placeholder} role="status">
                      {t("component.sidebar.global_search.label.no_dashboard")}
                    </div>
                    {/* Render button if not all loaded AND not triggered yet */}
                    {!hasLoadedAllData && (
                      <div className={styles.loadMoreButton}>
                        <Button
                          onClick={handleLoadMore}
                          buttonSize="small"
                          disabled={isLoadingMore}
                          aria-label="Load more dashboards"
                        >
                          {isLoadingMore ? (
                            <Loader className={styles.loader} />
                          ) : (
                            t("component.sidebar.global_search.load_more")
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className={styles.section}>
              <div className={styles.sectionItems} aria-label="Topic list">
                {isLoadingTrackers && sortedTrackers.length === 0 ? (
                  Array.from({ length: 5 }).map((_, index) => (
                    <div
                      className={styles.sectionItem}
                      key={`loading-track-${index}`}
                      role="progressbar"
                    >
                      <div className={styles.loadingItem}>
                        <Preloader type="bar" />
                      </div>
                    </div>
                  ))
                ) : sortedTrackers.length ? (
                  <List
                    ref={trackersListRef}
                    height={
                      sortedTrackers.length * ITEM_HEIGHT +
                      (!hasLoadedAllData ? ITEM_HEIGHT : 0)
                    }
                    itemCount={
                      sortedTrackers.length + (!hasLoadedAllData ? 1 : 0)
                    }
                    itemSize={ITEM_HEIGHT}
                    width="100%"
                    className={styles.virtualizedList}
                    outerElementType={ListContainer}
                  >
                    {({
                      index,
                      style,
                    }: {
                      index: number;
                      style: React.CSSProperties;
                    }) => {
                      if (isLoadingTrackers) {
                        return <LoadingItem style={style} />;
                      }

                      if (index === sortedTrackers.length) {
                        // Render "Load More" button if not all loaded AND not triggered yet
                        return !hasLoadedAllData ? (
                          <div className={styles.loadMoreButton} style={style}>
                            <Button
                              onClick={handleLoadMore}
                              buttonSize="small"
                              disabled={isLoadingMore}
                              aria-label="Load more topics"
                            >
                              {isLoadingMore ? (
                                <Loader className={styles.loader} />
                              ) : (
                                t("component.sidebar.global_search.load_more")
                              )}
                            </Button>
                          </div>
                        ) : null;
                      }

                      const tracker = sortedTrackers[index];
                      if (!tracker) return null;

                      return (
                        <TrackerItem
                          tracker={tracker}
                          index={index}
                          style={style}
                          getAuthorName={getAuthorFullName}
                          onTrackerClick={onTrackerClick}
                          onDeleteClick={onTrackerDeleteClick}
                          t={t}
                        />
                      );
                    }}
                  </List>
                ) : (
                  <div className={styles.placeholder} role="status">
                    {t("page.dashboard.label.no_topics")}
                  </div>
                )}
                {/* Show load more button for trackers tab even when list is empty */}
                {!hasLoadedAllData && !sortedTrackers.length && (
                  <div className={styles.loadMoreButton}>
                    <Button
                      onClick={handleLoadMore}
                      buttonSize="small"
                      disabled={isLoadingMore}
                      aria-label="Load more topics"
                    >
                      {isLoadingMore ? (
                        <Loader className={styles.loader} />
                      ) : (
                        t("component.sidebar.global_search.load_more")
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </Sidebar>
  );
};
